/**
 * lib/api/client.ts — browser-side fetchers for the public sound HTTP API.
 *
 * These are the client counterpart to the server-only `soundsApi`/`storageApi`
 * data layer. `'use client'` components MUST go through these (which hit
 * /api/sounds and /api/sounds/[id]) instead of importing soundsApi/storageApi
 * directly, so Supabase access stays on the server and each sound arrives with
 * a server-pre-resolved `audioUrl` (TES-831).
 */

import { type Sound, type Category } from '@/lib/supabase-types'

export interface SoundsListParams {
  category?: string
  search?: string
  page?: number
  perPage?: number
  sort?: 'created_at' | 'downloads' | 'title'
  order?: 'asc' | 'desc'
  signal?: AbortSignal
}

export interface SoundsListResult {
  sounds: Sound[]
  total: number
  page: number
  perPage: number
}

// Retry tuning: a small number of attempts with exponential backoff smooths
// over transient network blips and rate-limit/5xx responses without hammering
// the API. Kept conservative so a genuinely-down API fails fast.
const MAX_ATTEMPTS = 3
const BASE_DELAY_MS = 300
const MAX_RETRY_AFTER_MS = 10_000

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** Parse a Retry-After header (delta-seconds or HTTP-date) into milliseconds. */
function parseRetryAfter(header: string | null): number | null {
  if (!header) return null
  const seconds = Number(header)
  if (Number.isFinite(seconds)) {
    return Math.max(0, Math.min(seconds * 1000, MAX_RETRY_AFTER_MS))
  }
  const dateMs = Date.parse(header)
  if (!Number.isNaN(dateMs)) {
    return Math.max(0, Math.min(dateMs - Date.now(), MAX_RETRY_AFTER_MS))
  }
  return null
}

/** A 429 or any 5xx is worth retrying; everything else (incl. 4xx) is not. */
function isRetryableStatus(status: number): boolean {
  return status === 429 || (status >= 500 && status <= 599)
}

/**
 * fetch with retry-and-backoff. Retries on network errors and on retryable
 * status codes (429 / 5xx), honoring Retry-After when present. AbortError is
 * never retried. Returns the final Response (which may itself be non-OK) or
 * rethrows the last network error after exhausting attempts.
 */
async function fetchWithRetry(input: string, signal?: AbortSignal): Promise<Response> {
  let lastError: unknown
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetch(input, { signal })
      if (res.ok || !isRetryableStatus(res.status) || attempt === MAX_ATTEMPTS - 1) {
        return res
      }
      const retryAfter = parseRetryAfter(res.headers.get('retry-after'))
      await delay(retryAfter ?? BASE_DELAY_MS * 2 ** attempt, signal)
    } catch (error) {
      // Caller-initiated aborts must propagate immediately, not retry.
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      lastError = error
      if (attempt === MAX_ATTEMPTS - 1) throw error
      await delay(BASE_DELAY_MS * 2 ** attempt, signal)
    }
  }
  // Unreachable in practice (the loop always returns or throws), but keeps the
  // type checker happy.
  throw lastError ?? new Error('fetchWithRetry exhausted attempts')
}

/**
 * GET /api/sounds with the given filters. Retries transient failures
 * (network error / 429 / 5xx); final failures and aborts reject so callers can
 * distinguish an unavailable search from a successful search with no matches.
 */
export async function fetchSounds(params: SoundsListParams = {}): Promise<SoundsListResult> {
  const search = new URLSearchParams()
  if (params.category) search.set('category', params.category)
  if (params.search) search.set('search', params.search)
  if (params.page != null) search.set('page', String(params.page))
  if (params.perPage != null) search.set('per_page', String(params.perPage))
  if (params.sort) search.set('sort', params.sort)
  if (params.order) search.set('order', params.order)

  const qs = search.toString()
  const res = await fetchWithRetry(`/api/sounds${qs ? `?${qs}` : ''}`, params.signal)
  if (!res.ok) {
    throw new Error(`Failed to load sounds (HTTP ${res.status})`)
  }

  const data = (await res.json()) as {
    sounds?: Sound[]
    total?: number
    page?: number
    per_page?: number
  }
  return {
    sounds: data.sounds ?? [],
    total: data.total ?? 0,
    page: data.page ?? 1,
    perPage: data.per_page ?? (params.perPage ?? data.sounds?.length ?? 0),
  }
}

/**
 * GET /api/sounds/<id>. Resolves to null on 404 (sound not found) and on any
 * other failure after retries (network error / 429 / 5xx), mirroring
 * soundsApi.getSound's null-for-missing contract — callers never see a throw.
 * A 404 short-circuits immediately (no retry — the sound simply doesn't exist).
 */
export async function fetchSoundById(
  id: string,
  signal?: AbortSignal
): Promise<Sound | null> {
  let res: Response
  try {
    res = await fetchWithRetry(`/api/sounds/${encodeURIComponent(id)}`, signal)
  } catch {
    return null
  }
  if (res.status === 404) return null
  if (!res.ok) return null
  return (await res.json()) as Sound
}

/**
 * GET /api/categories. Retries transient failures (network error / 429 / 5xx);
 * on final failure returns an empty array rather than throwing, mirroring
 * soundsApi.getCategories' degrade-to-[] contract so callers never see a throw.
 */
export async function fetchCategories(signal?: AbortSignal): Promise<Category[]> {
  let res: Response
  try {
    res = await fetchWithRetry('/api/categories', signal)
  } catch {
    return []
  }
  if (!res.ok) {
    return []
  }
  const data = (await res.json()) as { categories?: Category[] }
  return data.categories ?? []
}
